import { useMutation } from "@apollo/client";
import { StaticGoogleMap, Marker } from "react-static-google-map";
import React, { useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";
import { Branch } from "../../../../models/partner_model";
import { lang } from "../../../../middleware/lang";
import {
  EditBranchVariables,
  EditBranchResponse,
  EDIT_BRANCH,
  GET_BRANCHES,
  CreateBranchResponse,
  CreateBranchVariables,
  CREATE_BRANCH,
  DELETE_BRANCH,
  DeleteBranch,
  DeleteBranchVariables,
} from "../queries";
import GoogleMap from "./GoogleMap";
import { DeleteModal } from "../../../modals/deletes";

type Props = {
  id: string | undefined;
  branch: Branch | undefined;
};

const STATE_NAMES: Record<string, string> = {
  "1": "Aguascalientes", "2": "Baja California", "3": "Baja California Sur",
  "4": "Campeche", "5": "Coahuila", "6": "Colima", "7": "Chiapas",
  "8": "Chihuahua", "9": "Distrito Federal", "10": "Durango",
  "11": "Guanajuato", "12": "Guerrero", "13": "Hidalgo", "14": "Jalisco",
  "15": "México", "16": "Michoacán", "17": "Morelos", "18": "Nayarit",
  "19": "Nuevo León", "20": "Oaxaca", "21": "Puebla", "22": "Querétaro",
  "23": "Quintana Roo", "24": "San Luis Potosí", "25": "Sinaloa",
  "26": "Sonora", "27": "Tabasco", "28": "Tamaulipas", "29": "Tlaxcala",
  "30": "Veracruz", "31": "Yucatán", "32": "Zacatecas", "33": "Ciudad de México",
};

const geocodeWithGoogleMaps = (
  query: string,
): Promise<{ lat: number; lng: number } | null> => {
  return new Promise((resolve) => {
    if (!(window as any).google?.maps?.Geocoder) {
      resolve(null);
      return;
    }
    const geocoder = new (window as any).google.maps.Geocoder();
    geocoder.geocode({ address: query }, (results: any[], status: string) => {
      if (status === "OK" && results.length > 0) {
        const loc = results[0].geometry.location;
        resolve({ lat: loc.lat(), lng: loc.lng() });
      } else {
        resolve(null);
      }
    });
  });
};

const BranchGeneral: React.FC<Props> = ({ id, branch }) => {
  const history = useHistory();
  const isEdit = !!id;
  const [adjusting, setAdjusting] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [location, setLocation] = useState("");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [whatsappPhone, setWhatsappPhone] = useState("");
  const [address, setAddress] = useState("");
  const [city, setCity] = useState("");
  const [zip, setZip] = useState("");
  const [state, setState] = useState("0");
  const [colonias, setColonias] = useState<string[]>([]);
  const [colonia, setColonia] = useState("");
  const [zipLoading, setZipLoading] = useState(false);
  const [zipError, setZipError] = useState("");
  const [contact, setContact] = useState("");
  const [privatePhone, setPrivatePhone] = useState("");
  const [geocoding, setGeocoding] = useState(false);
  const geocodeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [deleteBranch, { called: deleted, error: deleteError }] = useMutation<
    DeleteBranch,
    DeleteBranchVariables
  >(DELETE_BRANCH, {
    refetchQueries: [{ query: GET_BRANCHES }],
  });
  const [editBranch, { called, error }] = useMutation<
    EditBranchResponse,
    EditBranchVariables
  >(EDIT_BRANCH, {
    refetchQueries: [{ query: GET_BRANCHES }],
  });
  const [createBranch, { called: createCalled, error: createError }] =
    useMutation<CreateBranchResponse, CreateBranchVariables>(CREATE_BRANCH, {
      refetchQueries: [{ query: GET_BRANCHES }],
    });

  useEffect(() => {
    if (called && !error) {
      toast.success(lang("Sucursal actualizada", "Branch updated"));
      history.push("/branches");
    }
    if (createCalled && !createError) {
      toast.success(lang("Sucursal creada", "Branch created"));
      history.push("/branches");
    }
    if (deleted && !deleteError) {
      toast.success(lang("Sucursal eliminada", "Branch deleted"));
      history.push("/branches");
    }
  }, [history, called, error, createCalled, createError, deleted, deleteError]);

  useEffect(() => {
    if (!!branch) {
      const stringLocation = `${branch.location.latitude},${branch.location.longitude}`;
      const current = branch;
      setLocation(stringLocation);
      setName(current.name);
      setPhone(current.phone);
      setWhatsappPhone(current.whatsapp_phone || "");
      setZip(current.zip);
      setCity(current.city);
      setAddress(current.address);
      setState(current.state);
      setPrivatePhone(current.contact_phone);
      setContact(current.contact_name);
    }
  }, [branch]);

  const lookupZip = async (zipCode: string) => {
    if (zipCode.length !== 5) {
      setZipError(lang("El código postal debe tener 5 dígitos", "Zip code must be 5 digits"));
      return;
    }
    setZipLoading(true);
    setZipError("");
    setColonias([]);
    try {
      const res = await fetch(
        `https://sepomex.icalialabs.com/api/v1/zip_codes?zip_code=${zipCode}`,
      );
      const data = await res.json();
      if (!data.zip_codes || data.zip_codes.length === 0) {
        setZipError(lang("No se encontró información para este código postal", "No information found for this zip code"));
        setZipLoading(false);
        return;
      }
      const first = data.zip_codes[0];
      setState(String(parseInt(first.c_estado, 10)));
      setCity(first.d_ciudad || first.d_mnpio);
      const coloniasList = data.zip_codes.map((z: any) => z.d_asenta);
      setColonias(coloniasList);
      if (coloniasList.length === 1) {
        setColonia(coloniasList[0]);
      } else {
        setColonia("");
      }
    } catch {
      setZipError(lang("Error al buscar el código postal", "Error looking up zip code"));
    }
    setZipLoading(false);
  };

  const addressComplete = address.trim() && city.trim() && state !== "0";

  // Geocode when all address fields are filled (debounced)
  useEffect(() => {
    if (!addressComplete) return;
    if (geocodeTimerRef.current) clearTimeout(geocodeTimerRef.current);
    let cancelled = false;
    geocodeTimerRef.current = setTimeout(async () => {
      const stateName = STATE_NAMES[state] || "";
      const parts = [address, colonia, city, stateName, zip, "Mexico"].filter(Boolean);
      const query = parts.join(", ");
      setGeocoding(true);
      const coords = await geocodeWithGoogleMaps(query);
      if (cancelled) return;
      setGeocoding(false);
      if (coords) {
        setLocation(`${coords.lat},${coords.lng}`);
        setAdjusting(true);
      }
    }, 1000);
    return () => { cancelled = true; if (geocodeTimerRef.current) clearTimeout(geocodeTimerRef.current); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [address, city, state, colonia, zip]);

  const saveBranch = () => {
    if (!name.trim()) {
      toast.error(lang("El nombre es obligatorio", "Name is required"));
      return;
    }
    if (!location) {
      toast.error(lang("Debes seleccionar una ubicación en el mapa", "You must select a location on the map"));
      return;
    }
    const fullAddress = colonia ? `${address}, ${colonia}` : address;
    let data = {
      id: 0,
      name,
      phone,
      whatsapp_phone: whatsappPhone || null,
      address: fullAddress,
      city,
      zip,
      location,
      state: +state,
      contact_name: contact,
      contact_phone: privatePhone,
    };
    if (isEdit) {
      data.id = +id!;
      editBranch({ variables: { data } });
    } else {
      const { id, ...rest } = data;
      createBranch({ variables: { data: rest } });
    }
  };

  const sendDelete = () => {
    if (branch) {
      deleteBranch({ variables: { id: +branch.id } });
    }
  };

  const updateMarker = (coords: any) => {
    setLocation(`${coords.lat},${coords.lng}`);
    setAdjusting(false);
  };

  return (
    <>
      {/* Public info */}
      <div className="material_card p-3 mb-3">
        <h4>{lang("Datos Públicos", "Public Info")}</h4>
        <div className="form-group">
          <label htmlFor="branch_name">
            {lang("Nombre de la sucursal", "Branch name")} <span className="text-danger">*</span>
          </label>
          <input
            type="text"
            className="form-control"
            id="branch_name"
            placeholder={lang("Sucursal Centro", "Downtown Branch")}
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <small className="form-text text-muted">
            {lang("Máximo 100 caracteres", "Max 100 characters")}
          </small>
        </div>
        <div className="row">
          <div className="col-6">
            <div className="form-group">
              <label htmlFor="branch_phone">{lang("Teléfono al público", "Public phone")}</label>
              <input
                type="text"
                className="form-control"
                id="branch_phone"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
              />
              <small className="form-text text-muted">
                {lang("A qué número pueden marcar tus clientes?", "What number can your customers call?")}
              </small>
            </div>
          </div>
          <div className="col-6">
            <div className="form-group">
              <label htmlFor="branch_whatsapp">
                <i className="fab fa-whatsapp" style={{ color: "#25D366", marginRight: "0.4rem" }}></i>
                WhatsApp
              </label>
              <input
                type="text"
                className="form-control"
                id="branch_whatsapp"
                placeholder="5216621234567"
                value={whatsappPhone}
                onChange={(e) => setWhatsappPhone(e.target.value)}
              />
              <small className="form-text text-muted">
                {lang(
                  "Incluir código de país, ej. 5216621234567",
                  "Include country code, e.g. 5216621234567"
                )}
              </small>
            </div>
          </div>
        </div>
      </div>

      {/* Location */}
      <div className="material_card p-3 mb-3">
        <h4>{lang("Ubicación", "Location")}</h4>
        <div className="row">
          <div className="col-6">
            <div className="form-group">
              <label htmlFor="branch_zip">{lang("Código postal", "Zip code")}</label>
              <div className="input-group">
                <input
                  type="text"
                  className="form-control"
                  id="branch_zip"
                  maxLength={5}
                  value={zip}
                  onChange={(e) => {
                    const val = e.target.value.replace(/\D/g, "");
                    setZip(val);
                    if (val.length === 5) {
                      lookupZip(val);
                    }
                  }}
                />
                <div className="input-group-append">
                  <button
                    className="btn btn-outline-secondary"
                    type="button"
                    disabled={zipLoading}
                    onClick={() => lookupZip(zip)}
                  >
                    {zipLoading ? (
                      <i className="fas fa-spinner fa-spin"></i>
                    ) : (
                      <>
                        <i className="fas fa-search"></i>{" "}
                        {lang("Buscar", "Search")}
                      </>
                    )}
                  </button>
                </div>
              </div>
              {zipError && (
                <small className="form-text text-danger">{zipError}</small>
              )}
              <small className="form-text text-muted">
                {lang(
                  "Ingresa el código postal para llenar estado, ciudad y colonia automáticamente",
                  "Enter zip code to auto-fill state, city and neighborhood"
                )}
              </small>
            </div>
          </div>
          <div className="col-6">
            <div className="form-group">
              <label htmlFor="branch_state">{lang("Estado", "State")}</label>
              <select
                className="custom-select"
                id="branch_state"
                value={state}
                disabled={colonias.length > 0}
                onChange={(e) => setState(e.target.value)}
              >
                <option value="0">-</option>
                <option value="1">Aguascalientes</option>
                <option value="2">Baja California</option>
                <option value="3">Baja California Sur</option>
                <option value="4">Campeche</option>
                <option value="5">Coahuila de Zaragoza</option>
                <option value="6">Colima</option>
                <option value="7">Chiapas</option>
                <option value="8">Chihuahua</option>
                <option value="9">Distrito Federal</option>
                <option value="10">Durango</option>
                <option value="11">Guanajuato</option>
                <option value="12">Guerrero</option>
                <option value="13">Hidalgo</option>
                <option value="14">Jalisco</option>
                <option value="15">México</option>
                <option value="16">Michoacán de Ocampo</option>
                <option value="17">Morelos</option>
                <option value="18">Nayarit</option>
                <option value="19">Nuevo León</option>
                <option value="20">Oaxaca</option>
                <option value="21">Puebla</option>
                <option value="22">Querétaro</option>
                <option value="23">Quintana Roo</option>
                <option value="24">San Luis Potosí</option>
                <option value="25">Sinaloa</option>
                <option value="26">Sonora</option>
                <option value="27">Tabasco</option>
                <option value="28">Tamaulipas</option>
                <option value="29">Tlaxcala</option>
                <option value="30">Veracruz de Ignacio de la Llave</option>
                <option value="31">Yucatán</option>
                <option value="32">Zacatecas</option>
                <option value="33">Ciudad de México</option>
              </select>
            </div>
          </div>
        </div>
        <div className="row">
          <div className="col-6">
            <div className="form-group">
              <label htmlFor="branch_city">{lang("Ciudad", "City")}</label>
              <input
                type="text"
                className="form-control"
                id="branch_city"
                value={city}
                onChange={(e) => setCity(e.target.value)}
              />
            </div>
          </div>
          <div className="col-6">
            {colonias.length > 0 && (
              <div className="form-group">
                <label htmlFor="branch_colonia">{lang("Colonia", "Neighborhood")}</label>
                <select
                  className="custom-select"
                  id="branch_colonia"
                  value={colonia}
                  onChange={(e) => setColonia(e.target.value)}
                >
                  <option value="">{lang("Selecciona una colonia", "Select a neighborhood")}</option>
                  {colonias.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>
        </div>
        <div className="form-group">
          <label htmlFor="branch_address">{lang("Dirección", "Address")}</label>
          <input
            type="text"
            className="form-control"
            id="branch_address"
            placeholder={lang("Calle y número", "Street and number")}
            value={address}
            onChange={(e) => setAddress(e.target.value)}
          />
          <small className="form-text text-muted">
            {lang("Calle, número exterior e interior", "Street, exterior and interior number")}
          </small>
        </div>
        {!!location && !adjusting && (
          <>
            <StaticGoogleMap
              zoom={18}
              size="400x400"
              className="img-fluid"
              apiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY}
            >
              <Marker location={location} color="red" label="S" />
            </StaticGoogleMap>
            <button
              className="btn btn-outline-primary btn-sm mt-2"
              onClick={() => setAdjusting(true)}
            >
              <i className="fas fa-map-marker-alt"></i>{" "}
              {lang("Ajustar marcador", "Adjust marker")}
            </button>
          </>
        )}
        {!location && !geocoding && (
          <div className="alert alert-warning mt-2">
            <i className="fas fa-map-marker-alt mr-2"></i>
            {lang(
              "Completa la dirección, ciudad y estado para ubicar en el mapa automáticamente.",
              "Fill in address, city and state to auto-locate on the map."
            )}
          </div>
        )}
        {geocoding && (
          <div className="alert alert-info mt-2">
            <i className="fas fa-spinner fa-spin mr-2"></i>
            {lang("Buscando ubicación...", "Locating address...")}
          </div>
        )}
        {!!location && adjusting && (
          <GoogleMap
            lat={+location.split(",")[0]}
            lng={+location.split(",")[1]}
            updateMarker={updateMarker}
            disabled={!addressComplete}
          />
        )}
      </div>

      {/* Private info */}
      <div className="material_card p-3 mb-3">
        <h4>
          {lang("Datos Privados", "Private Info")}{" "}
          <small className="text-muted">
            <i className="fas fa-exclamation-triangle"></i>{" "}
            {lang("Solo Foo puede ver y usar estos datos", "Only Foo can see and use this data")}
          </small>
        </h4>
        <div className="row">
          <div className="col-6">
            <div className="form-group">
              <label htmlFor="branch_contact">{lang("Nombre de Contacto", "Contact Name")}</label>
              <input
                type="text"
                className="form-control"
                id="branch_contact"
                value={contact}
                onChange={(e) => setContact(e.target.value)}
              />
              <small className="form-text text-muted">
                {lang(
                  "Con quién nos podemos dirigir en caso de alguna duda o aclaración?",
                  "Who should we contact for questions or clarifications?"
                )}
              </small>
            </div>
          </div>
          <div className="col-6">
            <div className="form-group">
              <label htmlFor="contact_phone">{lang("Teléfono del Contacto", "Contact Phone")}</label>
              <input
                type="tel"
                className="form-control"
                id="contact_phone"
                value={privatePhone}
                onChange={(e) => setPrivatePhone(e.target.value)}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="d-flex justify-content-between mt-3">
        {isEdit && (
          <button
            className="btn btn-outline-danger"
            type="button"
            onClick={() => setShowDelete(true)}
          >
            <i className="fa fa-trash"></i>{" "}
            {lang("Eliminar", "Delete")}
          </button>
        )}
        <button
          className="btn btn-primary"
          type="button"
          id="save_branch_btn"
          onClick={saveBranch}
        >
          <i className="far fa-save"></i>{" "}
          {lang("Guardar", "Save")}
        </button>
      </div>

      <DeleteModal
        open={showDelete}
        title={lang(
          `¿Eliminar sucursal "${name}"?`,
          `Delete branch "${name}"?`
        )}
        toggle={() => setShowDelete(false)}
        handleConfirm={sendDelete}
        confirm_txt={lang("Eliminar", "Delete")}
      >
        <p>
          {lang(
            "Esta acción no se puede revertir",
            "This action cannot be undone"
          )}
        </p>
      </DeleteModal>
    </>
  );
};

export default BranchGeneral;
